<template>
  <main v-if="device" class="support-device-page">
    <section class="device-hero" :aria-labelledby="`${device.id}-title`">
      <div class="device-hero__visual">
        <img :src="device.coverUrl" :alt="`${device.name} device`">
      </div>
      <h1 :id="`${device.id}-title`">{{ device.name }}</h1>
    </section>

    <section class="support-resources" aria-labelledby="tutorials-title">
      <div class="support-detail-container">
        <div class="resource-grid" :aria-label="t('support.productDocuments')">
          <a
            v-if="device.productManualUrl"
            class="resource-entry"
            :href="device.productManualUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src="/static/images/support-detail/brochure.svg" alt="" aria-hidden="true">
            <h2>{{ t('support.brochure') }}</h2>
          </a>
          <a
            v-if="device.userManualUrl"
            class="resource-entry"
            :href="device.userManualUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src="/static/images/support-detail/manual.svg" alt="" aria-hidden="true">
            <h2>{{ t('support.manual') }}</h2>
          </a>
        </div>

        <h2 id="tutorials-title" class="tutorials-title">{{ t('support.tutorials') }}</h2>

        <div
          ref="tutorialViewport"
          class="tutorial-viewport"
          tabindex="0"
          :aria-label="t('support.tutorialsAria')"
          @scroll.passive="updateTutorialControls"
        >
          <div class="tutorial-track">
            <article
              v-for="tutorial in device.tutorials"
              :key="tutorial.id"
              class="tutorial-card"
              :class="{ 'tutorial-card--interactive': getTutorialAction(tutorial.content).type !== 'none' }"
              :aria-label="tutorial.title"
              :role="getTutorialAction(tutorial.content).type === 'none' ? null : 'button'"
              :tabindex="getTutorialAction(tutorial.content).type === 'none' ? null : 0"
              @click="handleTutorialClick(tutorial)"
              @keydown.enter.prevent="handleTutorialClick(tutorial)"
              @keydown.space.prevent="handleTutorialClick(tutorial)"
            >
              <img :src="tutorial.coverUrl" :alt="tutorial.title" loading="lazy" decoding="async">
              <span class="tutorial-card__shade" aria-hidden="true"></span>
              <span class="tutorial-card__play" aria-hidden="true">
                <span></span>
              </span>
            </article>
          </div>
        </div>

        <div class="tutorial-controls" :aria-label="t('support.tutorialControls')">
          <button
            class="legacy-carousel-button tutorial-control"
            type="button"
            :aria-label="t('support.previousTutorials')"
            :disabled="isTutorialBeginning"
            @click="scrollTutorials(-1)"
          >
            ←
          </button>
          <button
            class="legacy-carousel-button tutorial-control"
            type="button"
            :aria-label="t('support.nextTutorials')"
            :disabled="isTutorialEnd"
            @click="scrollTutorials(1)"
          >
            →
          </button>
        </div>
      </div>
    </section>

    <div v-if="tutorialVideoUrl" class="tutorial-preview" role="dialog" aria-modal="true" :aria-label="t('support.tutorials')" @click.self="closeTutorialPreview">
      <section class="tutorial-preview__dialog">
        <button class="tutorial-preview__close" type="button" :aria-label="t('navigation.close')" @click="closeTutorialPreview">×</button>
        <video ref="tutorialPreviewVideo" :src="tutorialVideoUrl" controls playsinline></video>
      </section>
    </div>

    <SupportFaq :categories="faqCategories" id-prefix="support-device-faq" />
  </main>
</template>

<script>
import SupportFaq from '@/components/SupportFaq.vue'
import { supportAPI } from '@/utils/api'

const {
  groupSupportDeviceFaqs,
  normalizeSupportDeviceDetailResponse,
} = require('@/utils/support-data.cjs')
const { siteLanguageMixin } = require('@/utils/site-language.js')
const { createLatestSiteRequestGuard } = require('@/utils/site-request.cjs')
const { getTutorialAction } = require('@/utils/tutorial-content.cjs')

export default {
  name: 'SupportDevice',
  components: {
    SupportFaq
  },
  mixins: [siteLanguageMixin],
  data() {
    return {
      device: null,
      faqCategories: [],
      isTutorialBeginning: true,
      isTutorialEnd: false,
      tutorialVideoUrl: '',
      supportRequestGuard: createLatestSiteRequestGuard()
    }
  },
  watch: {
    currentLanguage() {
      this.loadSupportDevice()
    },
    '$route.params.device': {
      immediate: true,
      handler() {
        this.loadSupportDevice()
      }
    }
  },
  mounted() {
    this.updateTutorialControls()
    window.addEventListener('resize', this.updateTutorialControls)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateTutorialControls)
    this.closeTutorialPreview()
  },
  methods: {
    async loadSupportDevice() {
      const request = this.supportRequestGuard.start(this.currentLanguage)
      this.device = null
      this.faqCategories = []
      try {
        const [device, faqCategories] = await Promise.all([
          supportAPI.getDevice(this.$route.params.device, request.siteType),
          supportAPI.getFaqs(request.siteType)
        ])
        if (!this.supportRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.device = normalizeSupportDeviceDetailResponse(device)
        this.faqCategories = groupSupportDeviceFaqs(faqCategories, this.device ? this.device.faqs : [])
        this.$nextTick(this.updateTutorialControls)
      } catch (error) {
        if (!this.supportRequestGuard.isCurrent(request, this.currentLanguage)) return
        console.error('Failed to load support device:', error)
      }
    },
    updateTutorialControls() {
      this.$nextTick(() => {
        const viewport = this.$refs.tutorialViewport
        if (!viewport) return

        const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth)
        this.isTutorialBeginning = viewport.scrollLeft <= 1
        this.isTutorialEnd = maxScrollLeft <= 1 || viewport.scrollLeft >= maxScrollLeft - 1
      })
    },
    scrollTutorials(direction) {
      const viewport = this.$refs.tutorialViewport
      if (!viewport) return

      const card = viewport.querySelector('.tutorial-card')
      const track = viewport.querySelector('.tutorial-track')
      const gap = track ? parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap) || 0 : 0
      const distance = card ? card.getBoundingClientRect().width + gap : viewport.clientWidth
      viewport.scrollBy({ left: direction * distance, behavior: 'smooth' })
    },
    getTutorialAction(content) {
      return getTutorialAction(content)
    },
    handleTutorialClick(tutorial) {
      const action = getTutorialAction(tutorial && tutorial.content)
      if (action.type === 'link') {
        window.open(action.url, '_blank', 'noopener')
        return
      }
      if (action.type === 'video') this.tutorialVideoUrl = action.url
    },
    closeTutorialPreview() {
      const player = this.$refs.tutorialPreviewVideo
      if (player) {
        player.pause()
        player.removeAttribute('src')
        player.load()
      }
      this.tutorialVideoUrl = ''
    }
  }
}
</script>

<style scoped>
.support-device-page {
  min-width: 0;
  padding-top: var(--global-header-height, 44px);
  overflow: hidden;
  color: #000;
  background: #fff;
}

.support-detail-container {
  width: min(1296px, calc(100% - 48px));
  margin: 0 auto;
}

.device-hero {
  height: 488px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 80px;
  box-sizing: border-box;
}

.device-hero__visual {
  width: 300px;
  height: 300px;
  display: grid;
  place-items: center;
  overflow: hidden;
  border-radius: 12px;
  background: #f5f5f7;
}

.device-hero__visual img {
  width: 118%;
  height: 118%;
  object-fit: contain;
}

.device-hero h1,
.resource-entry h2,
.tutorials-title {
  margin: 0;
}

.device-hero h1 {
  margin-top: 10px;
  font-size: 40px;
  font-weight: 700;
  line-height: 52px;
}

.support-resources {
  min-height: 712px;
  padding: 100px 0 80px;
  background: #f5f5f7;
  box-sizing: border-box;
}

.resource-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}

.resource-entry {
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 12px;
  background: #fff;
}

a.resource-entry {
  color: inherit;
  text-decoration: none;
}

.resource-entry img {
  width: 30px;
  height: 30px;
  object-fit: contain;
}

.resource-entry h2 {
  font-size: 20px;
  font-weight: 700;
  line-height: 30px;
}

@media (min-width: 701px) {
  .resource-entry:hover {
    background: #dedee3;
  }
}

.tutorials-title {
  margin-top: 108px;
  font-size: 40px;
  font-weight: 700;
  line-height: 52px;
  text-align: center;
}

.tutorial-viewport {
  margin-top: 48px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  scrollbar-width: none;
  scroll-snap-type: x mandatory;
}

.tutorial-viewport::-webkit-scrollbar {
  display: none;
}

.tutorial-viewport:focus-visible {
  outline: 2px solid #ef0a6a;
  outline-offset: 4px;
}

.tutorial-track {
  width: max-content;
  display: flex;
  gap: 30px;
}

.tutorial-card {
  position: relative;
  width: 329px;
  height: 185px;
  flex: 0 0 329px;
  overflow: hidden;
  border-radius: 12px;
  background: #fff;
  scroll-snap-align: start;
}

.tutorial-card--interactive {
  cursor: pointer;
}

.tutorial-card--interactive:focus-visible {
  outline: 2px solid #ef0a6a;
  outline-offset: 4px;
}

.tutorial-card > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.tutorial-card__shade {
  position: absolute;
  inset: 0;
  background: rgba(54, 54, 54, 0.5);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 160ms ease, visibility 160ms ease;
}

.tutorial-card__play {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.18);
  transform: translate(-50%, -50%);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 160ms ease, visibility 160ms ease;
}

.tutorial-card__play span {
  width: 0;
  height: 0;
  margin-left: 3px;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 8px solid #fff;
}

@media (min-width: 701px) {
  .tutorial-card--interactive:hover .tutorial-card__shade {
    background: rgba(54, 54, 54, 0.5);
    opacity: 1;
    visibility: visible;
  }

  .tutorial-card--interactive:hover .tutorial-card__play {
    opacity: 1;
    visibility: visible;
  }
}

.tutorial-preview {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.56);
  box-sizing: border-box;
}

.tutorial-preview__dialog {
  position: relative;
  width: min(900px, 100%);
  max-height: 100%;
}

.tutorial-preview__dialog video {
  width: 100%;
  max-height: calc(100vh - 96px);
  display: block;
  border-radius: 12px;
  background: #000;
}

.tutorial-preview__close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #fff;
  background: rgba(0, 0, 0, 0.56);
  font-size: 28px;
  line-height: 36px;
  cursor: pointer;
}

.tutorial-controls {
  display: flex;
  justify-content: flex-end;
  gap: 18px;
  margin-top: 31px;
}

.tutorial-control {
  width: 36px;
  height: 36px;
  font-size: 18px;
}

@media (max-width: 700px) {
  .support-detail-container {
    width: min(400px, calc(100vw - 32px));
    margin: 0 16px;
  }

  .device-hero {
    height: 470px;
    padding-top: 56px;
  }

  .device-hero__visual {
    width: 300px;
    height: 300px;
  }

  .device-hero h1 {
    margin-top: 10px;
    font-size: 40px;
    line-height: 52px;
  }

  .support-resources {
    height: 598px;
    min-height: 598px;
    padding: 56px 0 0;
  }

  .resource-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .resource-entry {
    height: 72px;
  }

  .tutorials-title {
    margin-top: 80px;
    font-size: 40px;
    line-height: 48px;
  }

  .tutorial-viewport {
    margin-top: 45px;
    overflow-x: auto;
  }

  .tutorial-track {
    width: max-content;
    gap: 20px;
  }

  .tutorial-card {
    width: 329px;
    height: 185px;
    flex: 0 0 329px;
    aspect-ratio: auto;
  }

  .tutorial-controls {
    justify-content: flex-end;
    margin-top: 20px;
  }
}

@media (max-width: 390px) {
  .support-detail-container {
    width: calc(100vw - 32px);
  }
}
</style>
